import FormInputComponent from "@via/components/inputs/form-input-component";
import Modal from "@via/components/modal";
import { useSetWorkbenchTimeout } from "@via/pages/workbenchs/workbenchs.api";
import { ModalSize } from "@via/utils/constants.tsx";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";

interface EditTimeoutModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  workbenchId: number;
  currentTimeoutExpiresAt: string | null;
}

interface FormData {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const EditTimeoutModal: FC<EditTimeoutModalProps> = ({
  open,
  setOpen,
  workbenchId,
  currentTimeoutExpiresAt,
}) => {
  const {
    mutateAsync: setWorkbenchTimeout,
    isPending: isSettingTimeoutLoading,
  } = useSetWorkbenchTimeout();

  // Calculate current timeout from expiresAt
  const calculateCurrentTimeout = () => {
    if (!currentTimeoutExpiresAt) {
      return { days: 0, hours: 1, minutes: 0, seconds: 0 };
    }

    const now = new Date().getTime();
    const expiry = new Date(currentTimeoutExpiresAt).getTime();
    const difference = expiry - now;

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
    );
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    return { days, hours, minutes, seconds };
  };

  const { control, handleSubmit, reset } = useForm<FormData>({
    mode: "onChange",
    defaultValues: calculateCurrentTimeout(),
  });

  useEffect(() => {
    if (open) {
      reset(calculateCurrentTimeout());
    }
  }, [open, currentTimeoutExpiresAt]);

  const handleCancel = () => {
    setOpen(false);
    reset();
  };

  // Resolving true hands the close to Modal. Invalid fields resolve false and keep
  // the dialog open on their errors, and a failed request rejects, which also keeps
  // it open with the mutation's notification on screen.
  //
  // This used to await `mutate`, which returns nothing, so the dialog closed the
  // moment the request was sent and a timeout that failed to save closed it anyway.
  const handleConfirmModal = async (): Promise<boolean> => {
    let saved = false;
    await handleSubmit(async (data: FormData) => {
      const totalSeconds =
        data.days * 24 * 60 * 60 +
        data.hours * 60 * 60 +
        data.minutes * 60 +
        data.seconds;

      await setWorkbenchTimeout({ id: workbenchId, totalSeconds });
      saved = true;
    })();
    return saved;
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Edit Timeout"
      confirmText="Save"
      size={ModalSize.Large}
      onCancel={handleCancel}
      isConfirmLoading={isSettingTimeoutLoading}
      onConfirm={handleConfirmModal}>
      <form
        onSubmit={e => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <div className="my-6">
          <p className="text-sm text-gray-700 mb-4">
            Set the timeout duration for this workbench instance.
          </p>

          <div className="grid grid-cols-4 gap-4">
            <FormInputComponent
              control={control}
              name="days"
              label="Days"
              type="number"
              min={0}
              max={10}
              placeholder="0"
              validations={{
                required: "Days is required",
                validate: {
                  min: (value: number) => value >= 0 || "Minimum is 0 days",
                  max: (value: number) => value <= 10 || "Maximum is 10 days",
                },
              }}
              showRequiredField={true}
              onKeyDown={e => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
            />

            <FormInputComponent
              control={control}
              name="hours"
              label="Hours"
              type="number"
              min={0}
              max={23}
              placeholder="0"
              validations={{
                required: "Hours is required",
                validate: {
                  min: (value: number) => value >= 0 || "Minimum is 0 hours",
                  max: (value: number) => value <= 23 || "Maximum is 23 hours",
                },
              }}
              showRequiredField={true}
              onKeyDown={e => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
            />

            <FormInputComponent
              control={control}
              name="minutes"
              label="Minutes"
              type="number"
              min={0}
              max={59}
              placeholder="0"
              validations={{
                required: "Minutes is required",
                validate: {
                  min: (value: number) => value >= 0 || "Minimum is 0 minutes",
                  max: (value: number) =>
                    value <= 59 || "Maximum is 59 minutes",
                },
              }}
              showRequiredField={true}
              onKeyDown={e => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
            />

            <FormInputComponent
              control={control}
              name="seconds"
              label="Seconds"
              type="number"
              min={0}
              max={59}
              placeholder="0"
              validations={{
                required: "Seconds is required",
                validate: {
                  min: (value: number) => value >= 0 || "Minimum is 0 seconds",
                  max: (value: number) =>
                    value <= 59 || "Maximum is 59 seconds",
                },
              }}
              showRequiredField={true}
              onKeyDown={e => {
                if (
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "+" ||
                  e.key === "-" ||
                  e.key === "."
                ) {
                  e.preventDefault();
                }
              }}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default EditTimeoutModal;
